import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Download, Check, Clock } from "react-feather";
import { AffiliatePayoutHistory } from "../../services/payout.service";
import { formatCurrency } from "../../utils/formatters";
import { format, isValid } from "date-fns";
import {
  A4,
  createDocument,
  downloadPdf,
  drawText,
  embedImageFromUrl,
  mm,
} from "../../utils/pdf";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";

export default function PayoutDetail() {
  const location = useLocation();
  const [payout] = useState<AffiliatePayoutHistory | null>(
    location.state?.payout || null
  );
  const [isLoading, setIsLoading] = useState(!location.state?.payout);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Not available";
    
    try {
      // Handle ISO date strings properly
      const date = new Date(dateString);
      return isValid(date) ? format(date, "MMM d, yyyy") : "Invalid date";
    } catch (error) {
      console.error("Date parsing error:", error);
      return "Invalid date format";
    }
  };

  const handleExport = async () => {
    if (!payout) return;

    // A4 portrait; layout measurements are in mm from the top-left corner.
    const { doc, font } = await createDocument();
    const page = doc.addPage([A4.width, A4.height]);
    const pageWidth = A4.width;
    const centre = pageWidth / 2;
    const marginLeft = mm(20);
    const marginRight = pageWidth - mm(20);

    // Company logo (skipped if it cannot be fetched or decoded)
    if (payout.company.brandProfileImage) {
      const logo = await embedImageFromUrl(doc, payout.company.brandProfileImage);
      if (logo) {
        page.drawImage(logo, {
          x: centre - mm(20),
          y: A4.height - mm(20) - mm(40),
          width: mm(40),
          height: mm(40),
        });
      }
    }

    // Title
    drawText(page, font, "Payout Receipt", {
      x: centre,
      y: mm(80),
      size: 20,
      align: "center",
    });

    // Company description (strip HTML tags)
    const companyDesc = payout.company.description.replace(/<[^>]*>/g, "");
    drawText(page, font, companyDesc, {
      x: centre,
      y: mm(95),
      size: 16,
      align: "center",
    });

    // Details
    const detailSize = 14;
    const startY = 130;
    const lineHeight = 10;
    const row = (index: number, label: string, value: string) => {
      const y = mm(startY + lineHeight * index);
      drawText(page, font, label, { x: marginLeft, y, size: detailSize });
      drawText(page, font, value, {
        x: marginRight,
        y,
        size: detailSize,
        align: "right",
      });
    };

    const paymentMethodName = payout.paymentDetails?.paymentMethodName || "---";
    let paymentDetailText = "---";
    if (payout.paymentDetails?.fieldData) {
      paymentDetailText = Object.values(payout.paymentDetails.fieldData)[0] || "---";
    }

    row(0, "Payout Amount:", formatCurrency(payout.amount));
    row(1, "Payment Method:", paymentMethodName.toLowerCase());
    row(2, "Status:", payout.status);
    row(3, "Payment Details:", paymentDetailText);

    // Footer
    drawText(page, font, `Generated on ${format(new Date(), "MMM d, yyyy HH:mm")}`, {
      x: centre,
      y: A4.height - mm(20),
      size: 10,
      align: "center",
    });

    downloadPdf(await doc.save(), `payout-${payout.id}.pdf`);
  };

  useEffect(() => {
    if (!location.state?.payout) {
      // If no payout data in state, redirect back to history
      window.location.href = "/affiliate/payouts/history";
    } else {
      setIsLoading(false);
    }
  }, [location.state?.payout]);

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 w-48 bg-muted rounded mb-6" />
        <div className="h-32 w-full bg-muted rounded-lg mb-4" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-muted rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!payout) {
    return (
      <EmptyState
        bordered={false}
        title="Payout not found"
        action={
          <Button asChild variant="outline">
            <Link to="/affiliate/payouts/history">Back to payout history</Link>
          </Button>
        }
      />
    );
  }

  // Strip HTML tags from company description
  const companyDesc = payout.company.description.replace(/<[^>]*>/g, "");

  return (
    <main>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <Link
              to="/affiliate/payouts/history"
              aria-label="Back to payout history"
              className="inline-flex items-center text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-2xl font-semibold tracking-tight">
              Payout details
            </h1>
          </div>
          <Button variant="ghost" onClick={handleExport}>
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>

        {/* Company Info */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={payout.company.brandProfileImage}
            alt={companyDesc}
            className="w-20 h-20 rounded-full mb-4"
          />
          <h2 className="text-lg font-semibold mb-1">
            {payout.company.companyName}
          </h2>
        </div>

        {/* Status Badge */}
        <div className="mb-8 flex justify-center">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${payout.status === "PAID" 
              ? "bg-green-50 text-green-700" 
              : "bg-yellow-50 text-yellow-700"}`}
          >
            {payout.status === "PAID" ? (
              <>
                <Check size={18} />
                <span>Paid</span>
              </>
            ) : (
              <>
                <Clock size={18} />
                <span>Pending</span>
              </>
            )}
          </div>
        </div>

        {/* Details List */}
        <div className="space-y-6 max-w-lg mx-auto">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Payout amount</h3>
            <p className="text-xl font-semibold">
              {formatCurrency(payout.amount)}
            </p>
            {payout.breakdown && payout.breakdown.recurringAmount > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                {formatCurrency(payout.breakdown.oneTimeAmount)} one-time and{" "}
                {formatCurrency(payout.breakdown.recurringAmount)} recurring
              </p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Payment method</h3>
            <p className="text-xl font-semibold capitalize">
              {payout.paymentDetails?.paymentMethodName?.toLowerCase() || "---"}
            </p>
          </div>

          {/* Payment Details */}
          {payout.paymentDetails?.fieldData && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Payment details</h3>
              <div className="bg-muted p-4 rounded-lg text-sm">
                {Object.entries(payout.paymentDetails.fieldData).map(([key, value]) => (
                  <div key={key} className="mb-2 last:mb-0">
                    <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}: </span>
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Date Information */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Request date</h3>
            <p className="text-base">
              {formatDate(payout.createdAt)}
            </p>
          </div>

          {payout.status === "PAID" ? (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Payment date</h3>
              <p className="text-base">
                {formatDate(payout.paidAt)}
              </p>
            </div>
          ) : null}
        </div>
    </main>
  );
}
